// react
import { memo } from "react";
import { useNavigate } from "react-router-dom";
//formik
import { useFormik, Form, FormikProvider } from "formik";
// mui components
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Slide from "@mui/material/Slide";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { LoadingButton } from "@mui/lab";
// user defined components, services, constants
import { authService } from "src/services";
import enums from "src/util/enums";
import { verficationValidationSchema } from "src/util/validationSchemas";
// hooks
import { useMutation } from "@tanstack/react-query";
import { useDispatch } from "react-redux";

//actions
import { alertSlice } from "src/store/alert/alert.slice";

function VerificationAndReset(props) {
    const { user, ...rest } = props;
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { mutateAsync, isLoading, reset } = useMutation(authService.verificationAndReset);

    const onSubmit = async (values, { setSubmitting }) => {
        const { otp, new_password } = values;
        const { actions: { setAlert } } = alertSlice
        await mutateAsync({ 
            username: user,
            otp,
            new_password: window.btoa(new_password)
         }, {
            onSuccess: () => {
                dispatch(setAlert({
                    open: true,
                    message: "Password reset successfully",
                    severity: enums.SEVERITY.SUCCESS,
                }));
                setTimeout(() => {
                    window.location.href = enums.path.LOGIN;
                }, 2000);
            },
            onError: (error) => {
                dispatch(setAlert({
                    open: true,
                    message: error?.response?.data?.message ?? error?.message,
                    severity: enums.SEVERITY.ERROR,
                }));
            },
            onSettled: () => reset()
        });
        setSubmitting(false);
    }

    const formik = useFormik({
        initialValues: {
          otp: "",
          old_password: "abc",
          new_password: "",
          confirm_password: ""
        },
        validationSchema: verficationValidationSchema,
        onSubmit,
    });

    const { errors, touched, handleSubmit, getFieldProps } = formik;

    return (
        <Slide {...rest}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <FormikProvider value={formik}>
                    <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
                        <Typography 
                            variant="h4" 
                            sx={{ 
                                mb: 2,
                                fontSize: { xs: '1.5rem', sm: '2rem' },
                                textAlign: 'center'
                            }}
                        >
                            Verification Required
                        </Typography>
                        <Typography 
                            variant="body2" 
                            sx={{ 
                                mb: 3,
                                textAlign: 'center',
                                color: 'text.secondary'
                            }}
                        >
                            To continue, complete this verification step. 
                            We've sent an OTP to the email associated with the username
                            <Typography className="username" variant="caption" fontSize="0.875rem" fontWeight={600}>{' '+ user}.</Typography>
                        </Typography>
                        <FormControl fullWidth variant="standard" sx={{ mb: 2 }}>
                            <Typography variant="caption" sx={{ mb: 1, fontWeight: 600 }}>
                                Enter OTP
                            </Typography>
                            <TextField 
                                size="small"
                                disabled={isLoading}
                                error={Boolean(touched.otp && errors.otp)}
                                helperText={touched.otp && errors.otp}
                                {...getFieldProps("otp")}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                    },
                                }}
                            />
                        </FormControl>
                        <FormControl fullWidth variant="standard" sx={{ mb: 2 }}>
                            <Typography variant="caption" sx={{ mb: 1, fontWeight: 600 }}>
                                Enter New Password
                            </Typography>
                            <TextField 
                                size="small"
                                type="password"
                                disabled={isLoading}
                                error={Boolean(touched.new_password && errors.new_password)}
                                helperText={touched.new_password && errors.new_password}
                                {...getFieldProps("new_password")}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                    },
                                }}
                            />
                        </FormControl>
                        <FormControl fullWidth variant="standard" sx={{ mb: 3 }}>
                            <Typography variant="caption" sx={{ mb: 1, fontWeight: 600 }}>
                                Confirm Password
                            </Typography>
                            <TextField 
                                size="small"
                                type="password"
                                disabled={isLoading}
                                error={Boolean(touched.confirm_password && errors.confirm_password)}
                                helperText={touched.confirm_password && errors.confirm_password}
                                {...getFieldProps("confirm_password")}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                    },
                                }}
                            />
                        </FormControl>
                        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                            <Button
                                variant="outlined"
                                fullWidth
                                onClick={() => navigate('/login')}
                                disabled={isLoading}
                                sx={{
                                    height: 48,
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                    borderColor: 'primary.main',
                                    color: 'primary.main',
                                    '&:hover': {
                                        borderColor: 'primary.dark',
                                        backgroundColor: 'primary.light',
                                    },
                                }}
                            >
                                Cancel
                            </Button>
                            <LoadingButton 
                                type="submit" 
                                fullWidth 
                                variant="contained" 
                                loading={isLoading}
                                sx={{
                                    height: 48,
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                }}
                            >
                                Continue
                            </LoadingButton>
                        </Box>
                    </Form>
                </FormikProvider>
            </CardContent>
        </Slide>
    )
}

VerificationAndReset.defaultProps = {
    container: null,
    in: false,
    direction: "left",
    timeout: 500,
    mountOnEnter: !0,
    unmountOnExit: !0
}

export default memo(VerificationAndReset);